/*
 * Copyright (C) 2025 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Component, Input, OnInit } from '@angular/core';
import { ChartResponderCell, SimpleChartData } from '@app/app/shared/components/carts/SimpleChartData';
import { LanguageCodeHelper } from '@app/app/shared/helpers/language-code-helper';
import { FeedbackEnrichedAnswer, FeedbackRecipient, IncludedFeedbackQuestion, Organization } from '@app/app/shared/models';

export enum ViewType {
  Bar = 'BAR',
  Horizontal = 'HORIZONTAL',
  Pie = 'PIE',
  Table = 'TABLE',
}

@Component({
  selector: 'ptl-feedback-providers-question-answer-choice',
  templateUrl: './question-answer-choice.component.html',
  styleUrls: ['./question-answer-choice.component.scss'],
})
export class QuestionAnswerChoiceComponent implements OnInit {
  @Input() question: IncludedFeedbackQuestion;
  @Input() feedbackRecipient: FeedbackRecipient;
  @Input() answers: FeedbackEnrichedAnswer[];
  @Input() language: string;
  @Input() organization: Organization;

  answer: FeedbackEnrichedAnswer;
  title: string;
  feedbackProviderResponsesCount: number;

  chartData: SimpleChartData[] = [];
  viewType: ViewType = ViewType.Table;

  readonly ViewType = ViewType;
  readonly chartOptions: { type: ViewType; icon: string }[] = [
    { type: ViewType.Table, icon: 'table_chart' },
    { type: ViewType.Bar, icon: 'sort' },
    { type: ViewType.Horizontal, icon: 'bar_chart' },
    { type: ViewType.Pie, icon: 'pie_chart' },
  ];

  ngOnInit(): void {
    if (!this.question) return;
    this.title = this.getInstruction();
    this.answer = this.getCurrentAnswer();
    this.feedbackProviderResponsesCount = this.getFeedbackProviderResponsesCount();
    this.chartData = this.getFormattedChartData();
  }

  onViewTypeChange(type: ViewType): void {
    this.viewType = type;
  }

  private getFeedbackProviderResponsesCount(): number {
    return this.answer?.optionScores?.reduce((sum, o) => sum + (o.selectedCount ?? 0), 0) ?? 0;
  }

  private getInstruction(): string {
    return LanguageCodeHelper.getDataByUserLanguageCode(this.question.question.instructions, this.organization, this.language)?.value ?? '';
  }

  private getCurrentAnswer(): FeedbackEnrichedAnswer {
    return this.answers.find((a) => a.questionUid === this.question.question.uid);
  }

  private getFormattedChartData(): SimpleChartData[] {
    const learnerAnswer = this.answer?.learnerQuestionAnswer;

    const recipientSelected = new Set<string>(learnerAnswer?.selectedOptions?.map((o) => o.optionUid) ?? []);

    const recipientCell =
      this.feedbackRecipient && learnerAnswer
        ? {
            id: this.feedbackRecipient.uid,
            name: [this.feedbackRecipient.firstName, this.feedbackRecipient.lastName].filter(Boolean).join(' '),
            imageUrl: this.feedbackRecipient.imageUrl,
            isRecipient: true,
            selected: false,
          }
        : null;

    return this.answer.optionScores.map(({ optionUid, optionLabel, averageScoreAcrossFeedbacks, selectedCount, feedbackProviders }) => {
      const isSelectedByRecipient = recipientSelected.has(optionUid);
      const recipientOption = learnerAnswer?.selectedOptions?.find((o) => o.optionUid === optionUid);
      const recipientScore = recipientOption?.userScore ?? null;

      const includeRecipient = this.feedbackRecipient && recipientScore !== null;

      const totalResponses = this.feedbackProviderResponsesCount + (includeRecipient ? 1 : 0);
      const adjustedSelectedCount = selectedCount + (includeRecipient && isSelectedByRecipient ? 1 : 0);

      let adjustedScore = averageScoreAcrossFeedbacks;
      if (includeRecipient && isSelectedByRecipient) {
        adjustedScore = (averageScoreAcrossFeedbacks * this.feedbackProviderResponsesCount + recipientScore) / totalResponses;
      }

      const providerCells = (feedbackProviders ?? []).map((p) => ({
        id: p.providerUid,
        name: p.name,
        imageUrl: p.imageUrl,
        selected: true,
      })) as ChartResponderCell[];

      const cells = recipientCell ? [{ ...recipientCell, selected: isSelectedByRecipient }, ...providerCells] : providerCells;

      return {
        label: optionLabel,
        score: adjustedScore,
        selectedCount: adjustedSelectedCount,
        allResponsesCount: totalResponses,
        responders: cells,
      } as SimpleChartData;
    });
  }
}
